import AppInput from '@nxsys/components/AppInput';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Col, Row, Typography } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import {
  CREDIT_LINE_KEYS,
  DEBIT_LINE_KEYS,
  JOURNAL_LINE_DESCRIPTION,
  JOURNAL_LINE_LABEL,
  JournalLineKey,
  MAX_CODE_LENGTH,
  MAX_DESCRIPTION_LENGTH,
  NominalMapping,
} from './constants';

type Props = {
  value: NominalMapping;
  onChange: (mapping: NominalMapping) => void;
  /** Keys that carry a non-zero amount in the current export; shown with * */
  requiredKeys?: JournalLineKey[];
  /** Keys to highlight as errors (blank code where one is needed). */
  missingKeys?: JournalLineKey[];
  disabled?: boolean;
};

/**
 * BrightPay-style two-column nominal code mapping: Debits on the left,
 * Credits on the right, one code + description per payroll data item.
 * Pure UI — shared by the export modal and (later) the Company Settings tab.
 */
const XeroNominalMappingForm: React.FC<Props> = ({
  value,
  onChange,
  requiredKeys = [],
  missingKeys = [],
  disabled = false,
}) => {
  const { messages } = useTypedIntl();
  const { isBureau } = useSelector((state: AppState) => state.appStore);
  // Umbrella has no Director NI yet (huddle 2026-09-08) — hide the mapping
  // row there; the journal itself still routes directors correctly the day
  // umbrella gains the feature. Bureau shows the row.
  const debitKeys = DEBIT_LINE_KEYS.filter(
    (key) => key !== 'DIRECTORS_REMUNERATION' || isBureau,
  );

  const update = (
    key: JournalLineKey,
    field: 'code' | 'description',
    text: string,
  ) => {
    onChange({
      ...value,
      [key]: { ...(value[key] ?? { code: '' }), [field]: text },
    });
  };

  const renderRow = (key: JournalLineKey) => {
    const entry = value[key];
    const isMissing = missingKeys.includes(key);
    const isRequired = requiredKeys.includes(key);
    return (
      <div key={key} className="mb-3">
        <Typography.Text italic className="block mb-1">
          {JOURNAL_LINE_LABEL[key]}
          {isRequired && <span className="text-red-500"> *</span>}
        </Typography.Text>
        <div className="flex gap-2">
          <AppInput
            className="!w-28"
            placeholder={messages['accounting.xeroJournal.code']}
            value={entry?.code ?? ''}
            onChange={(e) => update(key, 'code', e.target.value)}
            maxLength={MAX_CODE_LENGTH}
            allowCapitalize={false}
            status={isMissing ? 'error' : undefined}
            disabled={disabled}
          />
          <AppInput
            className="flex-1"
            placeholder={messages['accounting.xeroJournal.description']}
            value={entry?.description ?? ''}
            onChange={(e) => update(key, 'description', e.target.value)}
            maxLength={MAX_DESCRIPTION_LENGTH}
            allowCapitalize={false}
            disabled={disabled}
          />
        </div>
        <Typography.Text type="secondary" className="block mt-1 !text-[12px]">
          {JOURNAL_LINE_DESCRIPTION[key]}
        </Typography.Text>
      </div>
    );
  };

  return (
    <Row gutter={24}>
      <Col xs={24} md={12}>
        <Typography.Title level={5} className="!mb-3">
          {messages['accounting.xeroJournal.debits']}
        </Typography.Title>
        {debitKeys.map(renderRow)}
      </Col>
      <Col xs={24} md={12}>
        <Typography.Title level={5} className="!mb-3">
          {messages['accounting.xeroJournal.credits']}
        </Typography.Title>
        {CREDIT_LINE_KEYS.map(renderRow)}
      </Col>
    </Row>
  );
};

export default XeroNominalMappingForm;
